import {
  Box,
  Chip,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Switch,
  TextField,
  Theme,
  Typography,
} from '@material-ui/core';
import { Add, Delete, PlaylistAddCheck } from '@material-ui/icons';
import { createStyles, makeStyles } from '@material-ui/styles';
import React, { useEffect, useMemo, useState } from 'react';
import { ConsoleActionButton } from '../../common/ConsoleActionButton';
import { useApiUrl } from '../../../../../global/config/useApiUrl';
import { useSnackbar } from '../../../../../global/config/useSnackbar';
import { useCampfireFetch } from '../../../../../global/network/useCampfireFetch';
import { useCampfireQuery } from '../../../../../global/network/useCampfireQuery';
import { GET_MATCHING_SETTINGS } from './admin-console-matching.gql';
import { GetMatchingSettings } from './__generated__/GetMatchingSettings';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    ruleCard: {
      border: `1px solid ${theme.color.grey.border}`,
      borderRadius: 8,
      marginBottom: 16,
      overflow: 'hidden',
      backgroundColor: '#FFFFFF',
    },
    ruleHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '10px 8px 10px 16px',
      borderBottom: `1px solid ${theme.color.grey.border}`,
      backgroundColor: theme.color.white.neutral50,
    },
    ruleNameInput: {
      flex: 1,
      '&& .MuiInputBase-input': {
        fontSize: '0.875rem',
        fontWeight: 600,
        color: theme.color.grey.neutralBrand800,
        padding: 0,
      },
      '&& .MuiInputBase-input::placeholder': {
        fontWeight: 500,
        color: theme.color.grey.neutral200,
        opacity: 1,
      },
    },
    ruleBody: {
      padding: '16px',
    },
    ruleRow: {
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      marginBottom: 12,
    },
    ruleLabel: {
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      color: theme.color.grey.neutral200,
      width: 92,
      flex: '0 0 92px',
    },
    ruleHint: {
      fontSize: '0.75rem',
      color: '#999',
      marginLeft: 8,
    },
    selectControl: {
      minWidth: 200,
      marginRight: 8,
      '&& .MuiOutlinedInput-root': {
        borderRadius: 6,
        backgroundColor: '#FFFFFF',
        fontSize: '0.8rem',
      },
      '&& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#e0e0e0',
      },
      '&& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.color.grey.neutral200,
      },
      '&& .MuiSelect-select': {
        padding: '9px 32px 9px 12px',
      },
      '&& .MuiSelect-icon': {
        fontSize: 18,
        right: 8,
        color: theme.color.grey.neutral200,
      },
    },
    scoreField: {
      width: 68,
      '&& .MuiOutlinedInput-root': {
        borderRadius: 6,
        backgroundColor: '#FFFFFF',
        fontSize: '0.8rem',
      },
      '&& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#e0e0e0',
      },
      '&& .MuiOutlinedInput-input': {
        padding: '9px 10px',
        textAlign: 'center',
      },
    },
    menuItem: {
      fontSize: '0.8rem',
    },
    criteriaChip: {
      height: 28,
      fontSize: '0.8rem',
      borderRadius: 14,
      marginRight: 8,
      paddingLeft: 4,
      paddingRight: 4,
      border: `1px solid ${theme.color.grey.neutralBrand200}`,
      backgroundColor: '#FFFFFF',
      color: theme.color.grey.neutral300,
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: theme.color.grey.background,
      },
    },
    criteriaChipActive: {
      backgroundColor: theme.color.primary.extraLight,
      borderColor: '#af9dff',
      color: '#635ac7',
      fontWeight: 500,
      '&:hover': {
        backgroundColor: theme.color.primary.extraLight,
      },
    },
    sessionsBlock: {
      marginTop: 4,
      marginBottom: 14,
      paddingTop: 14,
      borderTop: `1px solid ${theme.color.grey.border}`,
    },
    sessionsHint: {
      fontSize: '0.75rem',
      color: '#999',
      marginTop: 2,
      marginBottom: 10,
    },
    sessionRow: {
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      marginBottom: 8,
    },
    sessionName: {
      width: 200,
      marginRight: 8,
      '&& .MuiOutlinedInput-root': {
        borderRadius: 6,
        backgroundColor: '#FFFFFF',
        fontSize: '0.8rem',
      },
      '&& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#e0e0e0',
      },
    },
    saveButton: {
      backgroundColor: theme.color.secondary.main900,
      color: '#ffffff',
      padding: '7px 12px',
      minWidth: 40,
    },
    ruleFooter: {
      display: 'flex',
      justifyContent: 'flex-end',
      paddingTop: 4,
    },
    emptyState: {
      border: `1px dashed ${theme.color.grey.neutralBrand200}`,
      borderRadius: 8,
      padding: '24px 16px',
      textAlign: 'center',
      fontSize: 13,
      color: theme.color.grey.neutral200,
      marginBottom: 16,
    },
    deleteButton: {
      color: theme.color.grey.neutral200,
      '&:hover': {
        color: theme.status.red.medium,
      },
    },
  })
);

interface DraftSession {
  clientKey: string;
  name: string;
  reportTypeId: string | null;
  isFlexible: boolean;
}

interface DraftRule {
  clientKey: string;
  matchingRuleId: string | null;
  name: string;
  applicantRoleId: string;
  counterpartRoleId: string;
  programId: string | null;
  matchOnAvailability: boolean;
  bidirectional: boolean;
  minimumScore: number;
  action: string;
  actionScheduling: string;
  actionFrequency: string;
  actionDurationMonths: number;
  sessions: DraftSession[];
  enabled: boolean;
  ruleOrder: number;
}

const ACTIONS = [{ value: 'create_paired_activity', label: 'Create a paired activity' }];

const SCHEDULING = [
  { value: 'flexible', label: 'Flexible' },
  { value: 'regular', label: 'Regular' },
];

const FREQUENCIES = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'fortnightly', label: 'Fortnightly' },
  { value: 'monthly', label: 'Monthly' },
];

const emptySession = (index: number): DraftSession => ({
  clientKey: `session-${Date.now()}-${index}`,
  name: '',
  reportTypeId: null,
  isFlexible: false,
});

const emptyRule = (order: number): DraftRule => ({
  clientKey: `new-${Date.now()}-${order}`,
  matchingRuleId: null,
  name: '',
  applicantRoleId: '',
  counterpartRoleId: '',
  programId: null,
  matchOnAvailability: true,
  bidirectional: false,
  minimumScore: 25,
  action: 'create_paired_activity',
  actionScheduling: 'flexible',
  actionFrequency: 'fortnightly',
  actionDurationMonths: 12,
  sessions: [{ ...emptySession(0), name: 'Meeting' }],
  enabled: true,
  ruleOrder: order,
});

interface Props {
  enabled: boolean;
}

export const MatchingRulesForm = ({ enabled }: Props) => {
  const classes = useStyles();
  const apiUrl = useApiUrl();
  const { setSnackbar } = useSnackbar();
  const ruleMutation = useCampfireFetch({ defer: true });

  const { data, refetch } = useCampfireQuery<GetMatchingSettings, undefined>(GET_MATCHING_SETTINGS);

  const [drafts, setDrafts] = useState<DraftRule[]>([]);

  const activityRoles = data?.vm.activityRoles ?? [];
  const programs = data?.vm.programs ?? [];
  const reportTypes = data?.vm.reportTypes ?? [];

  const serverRules = useMemo(
    () =>
      (data?.vm.matchingRules ?? []).map((rule) => ({
        clientKey: rule.matchingRuleId,
        matchingRuleId: rule.matchingRuleId,
        name: rule.name,
        applicantRoleId: rule.applicantRole.activityRoleId,
        counterpartRoleId: rule.counterpartRole.activityRoleId,
        programId: rule.program?.programId ?? null,
        matchOnAvailability: rule.matchOnAvailability,
        bidirectional: rule.bidirectional,
        minimumScore: rule.minimumScore,
        action: rule.action,
        actionScheduling: rule.actionScheduling,
        actionFrequency: rule.actionFrequency,
        actionDurationMonths: rule.actionDurationMonths,
        sessions: [...rule.sessions]
          .sort((a, b) => a.position - b.position)
          .map((session) => ({
            clientKey: session.matchingRuleSessionId,
            name: session.name,
            reportTypeId: session.reportType?.reportTypeId ?? null,
            isFlexible: session.isFlexible,
          })),
        enabled: rule.enabled,
        ruleOrder: rule.ruleOrder,
      })),
    [data]
  );

  /**
   * useCampfireQuery runs with fetchPolicy 'no-cache', so `data` is a new object
   * on every render. Seeding off a value signature rather than object identity
   * keeps in-progress edits and unsaved rules from being overwritten.
   */
  const serverSignature = JSON.stringify(serverRules);

  useEffect(() => {
    setDrafts(serverRules);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverSignature]);

  const updateDraft = (index: number, patch: Partial<DraftRule>) =>
    setDrafts((prev) => prev.map((rule, i) => (i === index ? { ...rule, ...patch } : rule)));

  const updateSession = (ruleIndex: number, sessionIndex: number, patch: Partial<DraftSession>) =>
    setDrafts((prev) =>
      prev.map((rule, i) =>
        i === ruleIndex
          ? {
              ...rule,
              sessions: rule.sessions.map((session, j) => (j === sessionIndex ? { ...session, ...patch } : session)),
            }
          : rule
      )
    );

  const addSession = (ruleIndex: number) =>
    setDrafts((prev) =>
      prev.map((rule, i) =>
        i === ruleIndex ? { ...rule, sessions: [...rule.sessions, emptySession(rule.sessions.length)] } : rule
      )
    );

  const removeSession = (ruleIndex: number, sessionIndex: number) =>
    setDrafts((prev) =>
      prev.map((rule, i) =>
        i === ruleIndex ? { ...rule, sessions: rule.sessions.filter((_unused, j) => j !== sessionIndex) } : rule
      )
    );

  const saveRule = async (rule: DraftRule) => {
    if (!rule.name.trim() || !rule.applicantRoleId || !rule.counterpartRoleId) {
      setSnackbar({ variant: 'error', open: true, message: 'Give the rule a name and both roles' });
      return;
    }

    if (rule.sessions.every((session) => !session.name.trim())) {
      setSnackbar({ variant: 'error', open: true, message: 'Add at least one session' });
      return;
    }

    const response = await ruleMutation.run({
      url: `${apiUrl}/vm/admin-console/matching-rules/save`,
      method: 'post',
      data: {
        matchingRuleId: rule.matchingRuleId,
        name: rule.name,
        applicantRoleId: rule.applicantRoleId,
        counterpartRoleId: rule.counterpartRoleId,
        programId: rule.programId,
        matchOnAvailability: rule.matchOnAvailability,
        bidirectional: rule.bidirectional,
        minimumScore: rule.minimumScore,
        action: rule.action,
        actionScheduling: rule.actionScheduling,
        actionFrequency: rule.actionFrequency,
        actionDurationMonths: rule.actionDurationMonths,
        sessions: rule.sessions
          .filter((session) => session.name.trim())
          .map((session) => ({
            name: session.name,
            reportTypeId: session.reportTypeId,
            isFlexible: session.isFlexible,
          })),
        enabled: rule.enabled,
        ruleOrder: rule.ruleOrder,
      },
    });

    if (response.ok) {
      setSnackbar({ variant: 'success', open: true, message: 'Settings updated' });
      refetch();
    } else {
      setSnackbar({ variant: 'error', open: true, message: 'Could not save matching rule' });
    }
  };

  const deleteRule = async (rule: DraftRule) => {
    if (!rule.matchingRuleId) {
      setDrafts((prev) => prev.filter((draft) => draft.clientKey !== rule.clientKey));
      return;
    }

    const response = await ruleMutation.run({
      url: `${apiUrl}/vm/admin-console/matching-rules/delete`,
      method: 'post',
      data: { matchingRuleId: rule.matchingRuleId },
    });

    if (response.ok) {
      setSnackbar({ variant: 'success', open: true, message: 'Settings updated' });
      refetch();
    } else {
      setSnackbar({ variant: 'error', open: true, message: 'Could not delete matching rule' });
    }
  };

  if (!enabled) {
    return null;
  }

  return (
    <Box>
      <>
        <Typography style={{ fontWeight: 600, fontSize: '16px', display: 'flex', alignItems: 'center' }}>
          <PlaylistAddCheck style={{ fontSize: '14px', paddingRight: '5px' }} /> Matching Rules
        </Typography>
        <Typography variant={'body2'} style={{ fontSize: '14px', color: '#808080' }}>
          {'Rules run in order. The first rule that covers a role the person holds provides the suggestions'}
        </Typography>

        <Box marginTop='1.5em'>
          {drafts.map((rule, index) => (
            <Box key={rule.clientKey} className={classes.ruleCard}>
              <Box className={classes.ruleHeader}>
                <TextField
                  className={classes.ruleNameInput}
                  placeholder='Name this rule'
                  value={rule.name}
                  onChange={(e) => updateDraft(index, { name: e.target.value })}
                  InputProps={{
                    disableUnderline: true,
                    style: { fontSize: '0.875rem', fontWeight: 600 },
                  }}
                />
                <Box display='flex' alignItems='center'>
                  <Switch
                    size='small'
                    color='primary'
                    checked={rule.enabled}
                    onChange={(e) => updateDraft(index, { enabled: e.target.checked })}
                    inputProps={{ 'aria-label': 'rule enabled checkbox' }}
                  />
                  <IconButton size='small' className={classes.deleteButton} onClick={() => deleteRule(rule)}>
                    <Delete style={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              </Box>

              <Box className={classes.ruleBody}>
                <Box className={classes.ruleRow}>
                  <Typography className={classes.ruleLabel}>When role</Typography>
                  <FormControl variant='outlined' size='small' className={classes.selectControl}>
                    <Select
                      style={{ fontSize: '0.8rem' }}
                      displayEmpty
                      value={rule.applicantRoleId}
                      onChange={(e) => updateDraft(index, { applicantRoleId: e.target.value as string })}
                    >
                      <MenuItem className={classes.menuItem} value='' disabled>
                        Select a role
                      </MenuItem>
                      {activityRoles.map((role) => (
                        <MenuItem className={classes.menuItem} key={role.activityRoleId} value={role.activityRoleId}>
                          {role.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                <Box className={classes.ruleRow}>
                  <Typography className={classes.ruleLabel}>Suggest</Typography>
                  <FormControl variant='outlined' size='small' className={classes.selectControl}>
                    <Select
                      style={{ fontSize: '0.8rem' }}
                      displayEmpty
                      value={rule.counterpartRoleId}
                      onChange={(e) => updateDraft(index, { counterpartRoleId: e.target.value as string })}
                    >
                      <MenuItem className={classes.menuItem} value='' disabled>
                        Select a role
                      </MenuItem>
                      {activityRoles.map((role) => (
                        <MenuItem className={classes.menuItem} key={role.activityRoleId} value={role.activityRoleId}>
                          {role.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                <Box className={classes.ruleRow}>
                  <Typography className={classes.ruleLabel}>Both ways</Typography>
                  <Switch
                    size='small'
                    color='primary'
                    checked={rule.bidirectional}
                    onChange={(e) => updateDraft(index, { bidirectional: e.target.checked })}
                    inputProps={{ 'aria-label': 'match both ways checkbox' }}
                  />
                  <Typography className={classes.ruleHint}>
                    {rule.bidirectional
                      ? 'Applicants in the suggested role are also offered matches in the first role'
                      : 'Only applicants in the first role are offered matches'}
                  </Typography>
                </Box>

                <Box className={classes.ruleRow}>
                  <Typography className={classes.ruleLabel}>In program</Typography>
                  <FormControl variant='outlined' size='small' className={classes.selectControl}>
                    <Select
                      style={{ fontSize: '0.8rem' }}
                      displayEmpty
                      value={rule.programId ?? ''}
                      onChange={(e) => updateDraft(index, { programId: (e.target.value as string) || null })}
                    >
                      <MenuItem className={classes.menuItem} value=''>
                        Same program as the applicant
                      </MenuItem>
                      {programs.map((program) => (
                        <MenuItem className={classes.menuItem} key={program.programId} value={program.programId}>
                          {program.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Typography className={classes.ruleHint}>
                    {rule.programId
                      ? 'Only volunteers in this program are suggested'
                      : 'One rule covers every program, pairing within the applicant’s own'}
                  </Typography>
                </Box>

                <Box className={classes.ruleRow}>
                  <Typography className={classes.ruleLabel}>Score on</Typography>
                  <Chip
                    label='Availability overlap'
                    className={`${classes.criteriaChip} ${rule.matchOnAvailability ? classes.criteriaChipActive : ''}`}
                    onClick={() => updateDraft(index, { matchOnAvailability: !rule.matchOnAvailability })}
                  />
                </Box>

                <Box className={classes.ruleRow}>
                  <Typography className={classes.ruleLabel}>Show above</Typography>
                  <TextField
                    size='small'
                    variant='outlined'
                    type='number'
                    className={classes.scoreField}
                    inputProps={{
                      min: 0,
                      max: 100,
                      style: { fontSize: '0.8rem', padding: '9px 10px', textAlign: 'center' },
                    }}
                    value={rule.minimumScore}
                    onChange={(e) => updateDraft(index, { minimumScore: Number(e.target.value) })}
                  />
                  <Typography className={classes.ruleHint}>% availability overlap</Typography>
                </Box>

                <Box className={classes.ruleRow}>
                  <Typography className={classes.ruleLabel}>On match</Typography>
                  <FormControl variant='outlined' size='small' className={classes.selectControl}>
                    <Select
                      style={{ fontSize: '0.8rem' }}
                      value={rule.action}
                      onChange={(e) => updateDraft(index, { action: e.target.value as string })}
                    >
                      {ACTIONS.map((action) => (
                        <MenuItem className={classes.menuItem} key={action.value} value={action.value}>
                          {action.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                <Box className={classes.ruleRow}>
                  <Typography className={classes.ruleLabel}>Schedule</Typography>
                  <FormControl variant='outlined' size='small' className={classes.selectControl}>
                    <Select
                      style={{ fontSize: '0.8rem' }}
                      value={rule.actionScheduling}
                      onChange={(e) => updateDraft(index, { actionScheduling: e.target.value as string })}
                    >
                      {SCHEDULING.map((option) => (
                        <MenuItem className={classes.menuItem} key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {rule.actionScheduling === 'regular' && (
                    <FormControl variant='outlined' size='small' className={classes.selectControl}>
                      <Select
                        style={{ fontSize: '0.8rem' }}
                        value={rule.actionFrequency}
                        onChange={(e) => updateDraft(index, { actionFrequency: e.target.value as string })}
                      >
                        {FREQUENCIES.map((option) => (
                          <MenuItem className={classes.menuItem} key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </Box>

                <Box className={classes.ruleRow}>
                  <Typography className={classes.ruleLabel}>Runs for</Typography>
                  <TextField
                    size='small'
                    variant='outlined'
                    type='number'
                    className={classes.scoreField}
                    inputProps={{
                      min: 1,
                      max: 120,
                      style: { fontSize: '0.8rem', padding: '9px 10px', textAlign: 'center' },
                    }}
                    value={rule.actionDurationMonths}
                    onChange={(e) => updateDraft(index, { actionDurationMonths: Number(e.target.value) })}
                  />
                  <Typography className={classes.ruleHint}>months by default</Typography>
                </Box>

                <Box className={classes.sessionsBlock}>
                  <Typography className={classes.ruleLabel} style={{ width: 'auto', flex: 'none' }}>
                    Sessions
                  </Typography>
                  <Typography className={classes.sessionsHint}>
                    The activity is created with these sessions, in order
                  </Typography>

                  {rule.sessions.map((session, sessionIndex) => (
                    <Box key={session.clientKey} className={classes.sessionRow}>
                      <TextField
                        size='small'
                        variant='outlined'
                        placeholder='Session name'
                        className={classes.sessionName}
                        inputProps={{ style: { fontSize: '0.8rem', padding: '9px 10px' } }}
                        value={session.name}
                        onChange={(e) => updateSession(index, sessionIndex, { name: e.target.value })}
                      />
                      <FormControl variant='outlined' size='small' className={classes.selectControl}>
                        <Select
                          style={{ fontSize: '0.8rem' }}
                          displayEmpty
                          value={session.reportTypeId ?? ''}
                          onChange={(e) =>
                            updateSession(index, sessionIndex, {
                              reportTypeId: (e.target.value as string) || null,
                            })
                          }
                        >
                          <MenuItem className={classes.menuItem} value=''>
                            No report
                          </MenuItem>
                          {reportTypes.map((reportType) => (
                            <MenuItem
                              className={classes.menuItem}
                              key={reportType.reportTypeId}
                              value={reportType.reportTypeId}
                            >
                              {reportType.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Chip
                        label='Flexible'
                        className={`${classes.criteriaChip} ${session.isFlexible ? classes.criteriaChipActive : ''}`}
                        onClick={() => updateSession(index, sessionIndex, { isFlexible: !session.isFlexible })}
                      />
                      <IconButton
                        size='small'
                        className={classes.deleteButton}
                        onClick={() => removeSession(index, sessionIndex)}
                      >
                        <Delete style={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                  ))}

                  <Box marginTop='4px'>
                    <ConsoleActionButton
                      variant='outlined'
                      color='primary'
                      startIcon={<Add style={{ fontSize: '16px' }} />}
                      onClick={() => addSession(index)}
                    >
                      Add Session
                    </ConsoleActionButton>
                  </Box>
                </Box>

                <Box className={classes.ruleFooter}>
                  <ConsoleActionButton
                    variant='contained'
                    color='primary'
                    className={classes.saveButton}
                    disabled={ruleMutation.isLoading}
                    onClick={() => saveRule(rule)}
                  >
                    Save Rule
                  </ConsoleActionButton>
                </Box>
              </Box>
            </Box>
          ))}

          {drafts.length === 0 && (
            <Box className={classes.emptyState}>{'No rules yet. Add one to say which roles get paired together'}</Box>
          )}

          <Box>
            <ConsoleActionButton
              variant='contained'
              color='primary'
              startIcon={<Add style={{ fontSize: '16px' }} />}
              onClick={() => setDrafts((prev) => [...prev, emptyRule(prev.length)])}
            >
              Add Rule
            </ConsoleActionButton>
          </Box>
        </Box>
      </>
    </Box>
  );
};
